import React from 'react';
import { Modal, ModalWrapper, ModalContent } from '@workboard/wobo-ui';
import styled from 'styled-components';
import { useTranslation, Trans } from 'react-i18next';
import TeamServices from '../../services/team.services';

const teamServices = new TeamServices();

const StyledModal = styled(Modal)`
  ${ModalWrapper} {
    width: 586px;
    border-radius: 4px;
  }
  ${ModalContent} {
    font-size: 14px;
    line-height: 21px;
    letter-spacing: 0;

    p {
      margin: 0;
    }

    div {
      padding-top: 15px;
      font-style: italic;
      color: rgba(0, 0, 0, 0.54);
    }
  }
`;

type ArchieveProps = {
  id: number;
  isActive: boolean;
  onClose: () => void;
  callTeam: (val) => void;
  text: string;
  teamType?: string;
};

const ArchieveModal = ({
  id,
  isActive,
  onClose,
  callTeam,
  text,
  teamType,
}: ArchieveProps): JSX.Element => {
  const { t } = useTranslation();
  const isRootOrFunctiontalTeam = +teamType == 0 || +teamType == 1;

  const onArchieveAction = (): void => {
    if (isRootOrFunctiontalTeam) {
      onClose();
      return;
    }
    const params = { teamId: id, archiveState: isActive };
    const message = isActive ? t('Team Unarchived.') : t('Team Archived.');

    teamServices
      .archieveTeams(params)
      .then((resp) => {
        if (resp.status) {
          onClose();
          toastr.success(message);
          callTeam(text);
        } else {
          toastr.error(
            `${t('Oops, something went wrong. Please try again later.')}`,
          );
        }
      })
      .catch(() => {
        toastr.error(
          `${t('Oops, something went wrong. Please try again later.')}`,
        );
      });
  };

  const submitArchiveLabel = isActive ? t('Unarchive') : t('Archive');
  const submitLabel = isRootOrFunctiontalTeam ? t('Ok') : submitArchiveLabel;

  return (
    <StyledModal
      className="archieve-conform"
      title={
        isActive ? t('Confirm Team Unarchiving') : t('Confirm Team Archiving')
      }
      isOpen
      onDismiss={onClose}
      onSubmit={onArchieveAction}
      submitLabel={submitLabel}
      data-testid="archieve-conform-testid"
    >
      {isRootOrFunctiontalTeam && (
        <p>
          <Trans
            i18nKey="team.archiveUnArchiveTeam"
            values={{ type: !isActive ? 'archive' : 'unarchive' }}
          />
        </p>
      )}
      {!isRootOrFunctiontalTeam && (
        <>
          {isActive ? (
            <p>
              {t(
                'Are you sure you want to unarchive the team? This action will not unarchive archived objectives, workstreams and AI&apos;s.',
              )}
            </p>
          ) : (
            <>
              <p>
                {t(
                  'Are you sure you want to archive this team? You will be able to unarchive the team at any point in time.',
                )}
              </p>
              <div>
                <strong>{t('NOTE')}:</strong>{' '}
                {t(
                  'We recommend that you Close and Rate any objectives that this team has been working on, prior to archiving the team. Workstreams will be archived and the OKRs will be closed out automatically.',
                )}
              </div>
            </>
          )}
        </>
      )}
    </StyledModal>
  );
};

ArchieveModal.defaultProps = { teamType: undefined };

export default ArchieveModal;
